import type { PresenceMember } from "@livcg/schema";
import { type ReactElement, useEffect, useState, useSyncExternalStore } from "react";
import { anchorLabel, findAnchor, VIEWPORT_ANCHOR } from "./anchor.js";
import { type CursorStore, STALE_MS } from "./cursorStore.js";

const LABEL_MAX = 40;
const VIEW_LABEL: Record<string, string> = {
  efir: "Efir",
  control: "Control",
  deck: "Deck",
  classic: "Klassik",
};

export function CursorLayer({
  cursors,
  members,
  selfId,
}: {
  cursors: CursorStore;
  members: PresenceMember[];
  selfId: string | null;
}) {
  const list = useSyncExternalStore(cursors.subscribe, cursors.snapshot);
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const byId = new Map(members.map((m) => [m.id, m]));
  const now = Date.now();
  const placed: ReactElement[] = [];
  const edge: ReactElement[] = [];
  for (const c of list) {
    const m = byId.get(c.id);
    if (!m || c.id === selfId || !c.anchor || now - c.at > STALE_MS) continue;
    let left: number | null = null;
    let top: number | null = null;
    if (c.anchor === VIEWPORT_ANCHOR) {
      left = c.x * innerWidth;
      top = c.y * innerHeight;
    } else {
      const el = findAnchor(c.anchor);
      if (el) {
        const r = el.getBoundingClientRect();
        left = r.left + c.x * r.width;
        top = r.top + c.y * r.height;
      }
    }
    if (left === null || top === null) {
      edge.push(
        <span
          key={c.id}
          className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-white shadow"
          style={{ backgroundColor: m.color }}
        >
          {m.username} → {VIEW_LABEL[m.view] ?? m.view} ·{" "}
          {anchorLabel(c.anchor).slice(0, LABEL_MAX)}
        </span>,
      );
      continue;
    }
    placed.push(
      <div
        key={c.id}
        className="absolute transition-transform duration-75 ease-linear"
        style={{ transform: `translate(${left}px, ${top}px)` }}
      >
        <svg width="16" height="20" viewBox="0 0 16 20" aria-hidden="true">
          <path
            d="M1 1 L1 16 L5 12 L8 19 L11 18 L8 11 L14 11 Z"
            fill={m.color}
            stroke="white"
            strokeWidth="1.2"
          />
        </svg>
        <span
          className="ml-3 -mt-1 inline-block rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-white shadow"
          style={{ backgroundColor: m.color }}
        >
          {m.username}
        </span>
      </div>,
    );
  }
  return (
    <div className="pointer-events-none fixed inset-0 z-[60]">
      {placed}
      {edge.length > 0 && (
        <div className="absolute bottom-3 right-3 flex flex-col items-end gap-1">{edge}</div>
      )}
    </div>
  );
}
